import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import MainA from '../../layout/Main Admin';
import Loader from '../../components/atoms/Loader';
import Table from '../../components/atoms/Table';
import Button from '../../components/atoms/Button';
import { getRequest } from '../../utils/axios';
import { NotificationManager } from 'react-notifications';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';

import moment from 'moment';
import * as XLSX from 'xlsx';

import './styles.scss';

const FormResponsesGroup = () => {
	const { formGroupId } = useParams();
	const navigate = useNavigate();
    const location = useLocation();
	const [loading, setLoading] = useState(true);
	const [responses, setResponses] = useState([]);
	const [expanded, setExpanded] = useState({});
	const [previewUrl, setPreviewUrl] = useState(null);

	const imageUrl = 'https://dynamicproductstorage.blob.core.windows.net/form/';

    const pageName = new URLSearchParams(location.search).get("pageName") || "Unknown Page";
    const groupName = new URLSearchParams(location.search).get("groupName") || "Unknown Group";

	useEffect(() => {
		if (!formGroupId) return;
		fetchResponses();
	}, [formGroupId]);

	const fetchResponses = async () => {
		setLoading(true);
		try {
			const resp = await getRequest(`FormResponse/GetByFormGroup/${formGroupId}`);
			setResponses(Array.isArray(resp.data) ? resp.data : []);
		} catch (err) {
			console.error('Error fetching form responses', err);
			NotificationManager.error('Failed to load form responses');
		} finally {
			setLoading(false);
		}
	};

	const toggleRow = async (formResponseId) => {
		if (!formResponseId) return;

		const cur = expanded[formResponseId];

		if (cur && cur.open) {
			setExpanded((prev) => ({ ...prev, [formResponseId]: { ...cur, open: false } }));
			return;
		}

		const closeAll = (state) => {
			const copy = { ...state };
			Object.keys(copy).forEach((k) => {
				copy[k] = { ...copy[k], open: false };
			});
			return copy;
		};

		if (cur && Array.isArray(cur.values) && cur.values.length > 0) {
			setExpanded((prev) => {
				const closed = closeAll(prev);
				return { ...closed, [formResponseId]: { ...cur, open: true } };
			});
			return;
		}

		setExpanded((prev) => {
			const closed = closeAll(prev);
			return { ...closed, [formResponseId]: { open: true, loading: true, values: [] } };
		});

		try {
			const resp = await getRequest(`FormResponseValue/GetByFormResponse/${formResponseId}`);
			const values = Array.isArray(resp.data) ? resp.data : [];
			setExpanded((prev) => {
				const closed = closeAll(prev);
				return { ...closed, [formResponseId]: { open: true, loading: false, values } };
			});
		} catch (err) {
			console.error('Error fetching response values', err);
			setExpanded((prev) => {
				const closed = closeAll(prev);
				return { ...closed, [formResponseId]: { open: true, loading: false, values: [] } };
			});
			NotificationManager.error('Failed to load response values');
		}
	};

	const exportToExcel = async () => {
		try {
			setLoading(true);
			const responsesToExport = Array.isArray(responses) ? responses : [];

			const promises = responsesToExport.map(async (r) => {
				try {
					const resp = await getRequest(`FormResponseValue/GetByFormResponse/${r.formResponseId}`);
					return { formResponseId: r.formResponseId, values: Array.isArray(resp.data) ? resp.data : [], meta: r };
				} catch (err) {
					return { formResponseId: r.formResponseId, values: [], meta: r };
				}
			});

			const all = await Promise.all(promises);

			// Determine field order
			const fieldOrder = [];
			const fieldSet = new Set();
			all.forEach((item) => {
				(item.values || []).forEach((v) => {
					const name = (v.formField && (v.formField.fieldName || v.formField.displayName)) || `Field ${v.formFieldId || ""}`;
					if (!fieldSet.has(name)) {
						fieldSet.add(name);
						fieldOrder.push(name);
					}
				});
			});

			const rows = all.map((item) => {
				const meta = item.meta || {};
				const row = {
					SubmittedBy: meta.submittedBy || "",
					Event: pageName || "",
					Group: groupName || "",
					SubmittedOn: meta.submittedDate ? moment(meta.submittedDate).format('MMMM DD, YYYY hh:mm A') : "",
				};

				(item.values || []).forEach((v) => {
					const name = (v.formField && (v.formField.fieldName || v.formField.displayName)) || `Field ${v.formFieldId || ""}`;
					row[name] = v.value ?? "";
				});

				fieldOrder.forEach((f) => { if (!(f in row)) row[f] = ""; });

				return row;
			});

			const header = ['SubmittedBy', 'Event', 'Group', 'SubmittedOn', ...fieldOrder];
			const wb = XLSX.utils.book_new();
			const ws = XLSX.utils.json_to_sheet(rows, { header });
			XLSX.utils.book_append_sheet(wb, ws, 'Responses');
			XLSX.writeFile(wb, `form-responses-group-${moment().format('YYYYMMDD_HHmmss')}.xlsx`);
		} catch (err) {
			console.error('Export error', err);
			NotificationManager.error('Failed to export responses');
		} finally {
			setLoading(false);
		}
	};

	return (
		<MainA className="b-form-responses-group-wrapper">
			<Loader loading={loading} />
			<div className="wrapper">
				<div className="head">
					<div className='title'>
						<h4>Form Responses</h4>
						<p>{pageName} - <span>{groupName}</span></p>
					</div>
					<div className="filters-row">
						<FontAwesomeIcon
									onClick={exportToExcel}
									icon={faFileExcel}
									title='Export Responses to Excel'
									style={{ color: '#1d6f42' }}
									className='excel-btn'
									/>
						<Button variant="outlined" onClick={() => navigate(-1)} aria-label="Go back">Back</Button>
					</div>
				</div>

				<div className='cards'>
					<div className='card'>
						<div className='card-title'>Total Responses</div>
						<div className='card-value'>{Array.isArray(responses) ? responses.length : 0}</div>
					</div>
				</div>

				{!loading && (
					<div className="responses-table">
						{Array.isArray(responses) && responses.length > 0 ? (
							<Table>
								<thead>
									<tr>
										<th />
										<th>Participant</th>
										<th>Submitted On</th>
									</tr>
								</thead>
								<tbody>
									{responses.sort((a, b) => new Date(b.submittedDate).getTime() - new Date(a.submittedDate).getTime()).map((r) => (
										<React.Fragment key={r.formResponseId}>
											<tr className="response-row" onClick={() => toggleRow(r.formResponseId)}>
												<td className="toggle-cell">
													<span className="toggle-icon" aria-hidden>
														{expanded[r.formResponseId] && expanded[r.formResponseId].open ? '−' : '+'}
													</span>
												</td>
												<td>{r.submittedBy || '-'}</td>
												<td>{r.submittedDate ? moment(r.submittedDate).format('MMMM DD, YYYY hh:mm A') : '-'}</td>
											</tr>
											{expanded[r.formResponseId] && expanded[r.formResponseId].open && (
												<tr className="expanded-row">
													<td colSpan={3}>
														{expanded[r.formResponseId].loading ? (
															<div>Loading response...</div>
														) : (
															<div className="expanded-content">
																{/* main values table on the left */}
																<div className="expanded-main">
																	{Array.isArray(expanded[r.formResponseId].values) && expanded[r.formResponseId].values.length > 0 ? (
																		<table className="v-small-table">
																			<thead>
																				<tr><th>Field</th><th>Value</th></tr>
																			</thead>
																			<tbody>
																				{expanded[r.formResponseId].values.map((v) => (
																					<tr key={v.formResponseValueId || v.formFieldId}>
																						<td>{(v.formField && (v.formField.fieldName || v.formField.displayName)) || `Field ${v.formFieldId || ''}`}</td>
																						<td>{v.value ?? '-'}</td>
																					</tr>
																				))}
																			</tbody>
																		</table>
																	) : (
																		<div>No values found for this response.</div>
																	)}
																</div>
																{/* right side: show uploaded payment image if present */}
																<div className="expanded-side">
																	{(() => {
																		const vals = expanded[r.formResponseId].values || [];
																		const imgVal = vals.find((v) => {
																			if (!v || !v.value) return false;
																			const s = String(v.value);
																			return /\.(jpg|jpeg|png|gif|webp|bmp|svg|tiff|tif|ico|avif|heic|heif|jfif)(\?|$)/i.test(s);
																		});
																		const url = imgVal ? imgVal.value : null;
																		return url ? (
																			<div className="payment-image-wrap">
																				<img src={`${imageUrl}${url}`} alt="payment upload" onClick={() => setPreviewUrl(`${imageUrl}${url}`)} style={{cursor: 'zoom-in'}} />
																			</div>
																			) : (
																			<div className="no-image">No payment image</div>
																			);
																		})()}
																</div>
															</div>
														)}
													</td>
												</tr>
											)}
										</React.Fragment>
									))}
								</tbody>
							</Table>
						) : (
							<div className="no-data">No responses found for this form group.</div>
						)}

						{/* image preview modal (single global overlay) */}
						{previewUrl && (
							<div className="image-preview-overlay" onClick={() => setPreviewUrl(null)}>
								<div className="image-preview-inner" onClick={(e) => e.stopPropagation()}>
									<img src={previewUrl} alt="preview" />
								</div>
							</div>
						)}
					</div>
				)}
			</div>
		</MainA>
	);
};

export default FormResponsesGroup;
